import { IAutomationSchedule } from '@dadohr/shared/domainTypes/automations';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { ScheduleStatus } from '@dadohr/shared/domainTypes/schedules';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  RowsRendererScrollContainer,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { Loader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import { useAutomationSchedulesByExperienceTemplateId } from '../../../../../../../services/automations';
import { updateDoc } from '../../../../../../../services/firebase';
import {
  MomentRange,
  formatDatePrecise,
  toMoment
} from '../../../../../../../services/time';
import { JsonDialogInner } from '../../../../../../components/Json';
import { toFirestoreConsoleFromDoc } from '../../../../../../services/firebase';
import { publishTopic } from '../../../../../../services/pubsub';

type ColumnName =
  | 'experienceId'
  | 'status'
  | 'at'
  | 'attempts'
  | 'finishedAt'
  | 'actions';

type D = Doc<IAutomationSchedule>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      },
      {
        key: 'execute',
        label: 'Execute',
        disabled:
          d.data.status !== ScheduleStatus.PENDING &&
          d.data.status !== ScheduleStatus.RETRY,
        onClick: () =>
          publishTopic({
            topic: 'schedules-executeSchedule',
            payload: {
              scheduleId: d.id,
              ts: Date.now()
            }
          })
      },
      {
        key: 'resetRetry',
        label: 'Reset retry attempts',
        disabled:
          d.data.status !== ScheduleStatus.ERROR &&
          d.data.status !== ScheduleStatus.RETRY,
        onClick: () =>
          updateDoc(d, () => ({ attempts: 0, status: ScheduleStatus.RETRY }))
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'experienceId',
    head: () => 'Exp Id',
    cell: (d) => d.data.experienceId,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => d.data.status,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'at',
    head: () => 'at',
    cell: (d) => formatDatePrecise(toMoment(d.data.at)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'attempts',
    head: () => '#',
    cell: (d) => d.data.attempts,
    sortable: true,
    align: 'right',
    width: 50,
    flexGrow: 0
  },
  {
    key: 'finishedAt',
    head: () => 'finished',
    cell: (d) =>
      d.data.finishedAt ? formatDatePrecise(toMoment(d.data.finishedAt)) : null,
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];
const SORTERS: ItemSorters<D> = {
  attempts: {
    key: 'attempts',
    items: { sort: (d) => d.data.attempts, dir: 'desc' }
  },
  at: {
    key: 'at',
    items: { sort: (d) => d.data.at.toMillis(), dir: 'asc' }
  },
  finishedAt: {
    key: 'at',
    items: { sort: (d) => d.data.finishedAt?.toMillis() || 0, dir: 'asc' }
  }
};

const DEFAULT_SORTER = SORTERS.at;
const DAYS_TO_LOOK_AHEAD = 21;

export const UpcomingAutomationSchedules = ({
  tpl
}: {
  tpl: Doc<IExperienceTemplate>;
}) => {
  const range: MomentRange = useMemo(() => {
    return {
      start: moment().startOf('d').subtract(1, 'd'),
      end: moment().startOf('d').add(DAYS_TO_LOOK_AHEAD, 'd')
    };
  }, []);

  const [ds, loading, error] = useAutomationSchedulesByExperienceTemplateId(
    tpl.data.orgId,
    tpl.id,
    range
  );

  useErrorLogger('Automation Schedules', error);

  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  return (
    <Section>
      <CanvasBar>
        <div>
          Recent Automation Schedules (yesterday + next {DAYS_TO_LOOK_AHEAD}{' '}
          days)
        </div>
        <div>
          <ButtonWithPromise
            color="secondary"
            variant="contained"
            onClick={() =>
              publishTopic({
                topic: 'automationSchedules-scheduledCheckForOrganization',
                payload: {
                  orgId: tpl.data.orgId,
                  tplId: tpl.id
                }
              })
            }
            pending="Starting..."
          >
            Force run
          </ButtonWithPromise>
        </div>
      </CanvasBar>
      {loading && <Loader height={500} />}
      {ds && (
        <RowsRendererScrollContainer height={500}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={ds}
            rowToKey={ID_TO_ROW_KEY}
            sorter={sorter || DEFAULT_SORTER}
            sortDirection={dir}
            renderHead={true}
            headProps={{ sticky: true }}
            onHeadClick={(c, d) =>
              setSort([SORTERS[c.key] || DEFAULT_SORTER, d])
            }
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={undefined}
          />
        </RowsRendererScrollContainer>
      )}
    </Section>
  );
};
